'use client';

import { JSX } from 'react';
import { SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import orTestimonialBlock from './or-testimonial-block.variants';
import classNames from 'classnames';
import 'swiper/css/effect-fade';

// components
import { IMlTestimonialBlockCard } from '@/components/molecules/ml-testimonial-block-card/ml-testimonial-block-card.types';
import { MlTestimonialBlockCard } from '@/components/molecules/ml-testimonial-block-card/ml-testimonial-block-card';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtCarousel } from '@/components/atoms/at-carousel/at-carousel';

// types
import { IOrTestimonialBlockProps } from './or-testimonial-block.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';

/**
 * Renders a testimonial carousel with optional top and bottom spacing and dividers.
 *
 * - Accepts an array of testimonial `cards`, each rendered via `MlTestimonialBlockCard`.
 * - Uses the `AtCarousel` atom to enable Swiper-powered slide transitions.
 * - When `autoplay` is enabled, slides rotate every 5 seconds.
 * - Applies spacing via `orTestimonialBlock` Tailwind Variants.
 * - Supports optional horizontal dividers above and below the carousel.
 *
 * Layout:
 * - Centers the carousel content within a 12-column grid
 * - Carousel buttons are positioned **below** the content
 * - Uses `fade` transition effect between slides
 *
 */
export const OrTestimonialBlock = ({
  cards,
  autoplay,
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
}: IOrTestimonialBlockProps): JSX.Element => {
  const classes = orTestimonialBlock({ topSpacing, bottomSpacing });
  // todo: update theme to dynamic val
  return (
    <div data-theme="light">
      <section className="container">
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
        <div className={classNames('container', classes)}>
          <AtCarousel
            autoplay={autoplay ? { delay: 5000 } : false}
            ariaLabel={`Promotional carousel with ${cards.length} slides`}
            buttonPosition="below"
            containerClasses="grid grid-cols-12"
            contentClasses="col-span-full lg:col-start-3 lg:col-span-8"
            effect="fade"
            fadeEffect={{ crossFade: true }}
            intent="testimonial"
            modules={[Autoplay, EffectFade]}
            slidesPerView={1}
            swiperClasses="mb-60 md:mb-0"
          >
            {cards?.map((card: IMlTestimonialBlockCard, index: number) => {
              const { images, headline, quote, cta } = card;
              return (
                <SwiperSlide key={index} className="py-3">
                  <MlTestimonialBlockCard
                    cta={cta}
                    headline={headline}
                    images={images}
                    quote={quote}
                  />
                </SwiperSlide>
              );
            })}
          </AtCarousel>
        </div>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
      </section>
    </div>
  );
};
